/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { examApi, userApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Exam, User, UserRole } from '../types';

const AssignExam: React.FC = () => {
    const { examId } = useParams<{ examId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [exam, setExam] = useState<Exam | null>(null);
    const [students, setStudents] = useState<User[]>([]);
    const [selectedStudents, setSelectedStudents] = useState<Set<number>>(new Set());
    const [allStudents, setAllStudents] = useState<User[]>([]);
    const [alreadyAssignedStudents, setAlreadyAssignedStudents] = useState<Set<number>>(new Set());
    const [unassigningIds, setUnassigningIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch exam details
                const examData = await examApi.getById(Number(examId));
                setExam(examData);

                // Fetch all students (users with STUDENT role), restrict to teacher's institution
                const allUsers = await userApi.getAll();
                let studentsOnly = allUsers.filter((u: User) => u.role === 'STUDENT');
                if (user?.institutionId) {
                    studentsOnly = studentsOnly.filter((u: User) => u.institutionId === user.institutionId);
                }
                setAllStudents(studentsOnly);
                setStudents(studentsOnly);

                // Fetch already assigned students
                const assignedStudents = await examApi.getAssignedStudents(Number(examId));
                const assignedIds = new Set(assignedStudents.map((s: User) => s.id!));
                setAlreadyAssignedStudents(assignedIds);
                // Do not preselect already-assigned students; teacher can select them to assign next attempt
                setSelectedStudents(new Set());
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (examId) {
            fetchData();
        }
    }, [examId]);

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        if (term.trim() === '') {
            setStudents(allStudents);
        } else {
            const termLower = term.toLowerCase();
            const filtered = allStudents.filter((student) =>
                student.name.toLowerCase().includes(termLower) ||
                (student.username ? student.username.toLowerCase().includes(termLower) : false) ||
                (student.email ? student.email.toLowerCase().includes(termLower) : false)
            );
            setStudents(filtered);
        }
    };

    const handleSelectStudent = (studentId: number) => {
        const newSelected = new Set(selectedStudents);
        // Toggle selection. If the student is already assigned, selecting them means assign next attempt.
        if (newSelected.has(studentId)) {
            newSelected.delete(studentId);
        } else {
            newSelected.add(studentId);
        }
        setSelectedStudents(newSelected);
    };

    const handleSelectAll = () => {
        const newSelected = new Set(alreadyAssignedStudents);
        // Add/remove non-assigned students
        if (selectedStudents.size === students.length) {
            // Unselect only non-assigned students
            setSelectedStudents(newSelected);
        } else {
            // Select all students
            students.forEach(s => newSelected.add(s.id!));
            setSelectedStudents(newSelected);
        }
    };

    const handleUnassign = async (studentId: number) => {
        if (!user) { setError('You must be logged in'); return; }
        if (!examId) return;
        const confirmMsg = 'Are you sure you want to unassign this exam from the student?';
        if (!window.confirm(confirmMsg)) return;
        const nextSet = new Set(unassigningIds); nextSet.add(studentId); setUnassigningIds(nextSet);
        setError(null);
        try {
            await examApi.unassignExam(Number(examId), studentId, user.id);
            // Update local state: remove from alreadyAssigned and from selected
            setAlreadyAssignedStudents(prev => { const ns = new Set(prev); ns.delete(studentId); return ns; });
            setSelectedStudents(prev => { const ns = new Set(prev); ns.delete(studentId); return ns; });
        } catch (err) {
            console.error('Failed to unassign', err);
            setError('Failed to unassign. Please try again.');
        } finally {
            setUnassigningIds(prev => { const ns = new Set(prev); ns.delete(studentId); return ns; });
        }
    };

    const handleAssign = async () => {
        if (selectedStudents.size === 0) {
            setError('Please select at least one student');
            return;
        }

        setAssigning(true);
        setError(null);
        
        try {
            // Call the backend API to assign exam to students
            await examApi.assignExam({
                examId: Number(examId),
                studentIds: Array.from(selectedStudents)
            });
            
            setSuccessMessage(`✅ Exam assigned to ${selectedStudents.size} student(s) successfully!`);
            setTimeout(() => {
                navigate(`/`);
            }, 2000);
        } catch (err) {
            console.error('Error assigning exam:', err);
            setError('Failed to assign exam. Please try again.');
        } finally {
            setAssigning(false);
        }
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!exam) {
        return (
            <div className="empty-state">
                <div className="empty-state-emoji">😕</div>
                <h3 className="empty-state-title">Exam Not Found</h3>
                <button onClick={() => navigate('/')} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                    ← Go Back
                </button>
            </div>
        );
    }

    return (
        <div data-testid="assign-exam-page">
            <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    👥 Assign Exam: {exam.title}
                </h1>
                <p style={{ opacity: 0.9 }}>
                    Select students to assign this exam
                </p>
            </div>

            {error && (
                <div style={{
                    backgroundColor: '#fed7d7',
                    color: '#c53030',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    border: '1px solid #fc8181'
                }}>
                    ❌ {error}
                </div>
            )}

            {successMessage && (
                <div style={{
                    backgroundColor: '#c6f6d5',
                    color: '#22543d',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    border: '1px solid #9ae6b4'
                }}>
                    {successMessage}
                </div>
            )}

            <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                    <input
                        type="text"
                        placeholder="🔍 Search students by name, username, or email..."
                        className="form-input"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        data-testid="student-search"
                    />
                </div>

                <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: '600' }}>
                        <input
                            type="checkbox"
                            checked={selectedStudents.size === students.length && students.length > 0}
                            onChange={handleSelectAll}
                            style={{ marginRight: '0.75rem', cursor: 'pointer', width: '18px', height: '18px' }}
                            data-testid="select-all-checkbox"
                        />
                        Select All ({selectedStudents.size}/{students.length})
                    </label>
                </div>

                {students.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
                        <p>No students found</p>
                    </div>
                ) : (
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {students.map((student) => {
                            const isAlreadyAssigned = alreadyAssignedStudents.has(student.id!);
                            return (
                            <label
                                key={student.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0.75rem',
                                    borderRadius: '6px',
                                    cursor: isAlreadyAssigned ? 'default' : 'pointer',
                                    backgroundColor: selectedStudents.has(student.id!)
                                        ? isAlreadyAssigned ? '#d4edda' : '#edf2f7'
                                        : 'transparent',
                                    marginBottom: '0.5rem',
                                    border: '1px solid ' + (selectedStudents.has(student.id!)
                                        ? isAlreadyAssigned ? '#28a745' : '#667eea'
                                        : 'transparent'),
                                    opacity: isAlreadyAssigned ? 0.8 : 1
                                }}
                                data-testid={`student-${student.id}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedStudents.has(student.id!)}
                                    onChange={() => handleSelectStudent(student.id!)}
                                    style={{ marginRight: '0.75rem', cursor: 'pointer', width: '18px', height: '18px' }}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                        <div style={{ fontWeight: '600', color: '#2d3748', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            {student.name}
                                            {isAlreadyAssigned && (
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    backgroundColor: '#28a745',
                                                    color: 'white',
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '4px'
                                                }}>
                                                    ✅ Assigned • Select to assign next attempt
                                                </span>
                                            )}
                                        </div>
                                        {isAlreadyAssigned && user && user.role !== 'STUDENT' && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-xs"
                                                onClick={(e) => { e.stopPropagation(); handleUnassign(student.id!); }}
                                                disabled={unassigningIds.has(student.id!)}
                                                title="Unassign exam from this student"
                                            >
                                                {unassigningIds.has(student.id!) ? 'Removing...' : 'Unassign'}
                                            </button>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                                        @{(student.username ?? student.email)} • {student.email}
                                    </div>
                                </div>
                            </label>
                            );
                        })}
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                    onClick={() => navigate('/')}
                    className="btn btn-outline"
                    style={{ flex: 1 }}
                    data-testid="cancel-button"
                >
                    ← Cancel
                </button>
                <button
                    onClick={handleAssign}
                    className="btn btn-success"
                    style={{ flex: 1 }}
                    disabled={assigning || selectedStudents.size === 0}
                    data-testid="assign-button"
                >
                    {assigning ? '⌛ Assigning...' : `✅ Assign to ${selectedStudents.size} Student(s)`}
                </button>
            </div>
        </div>
    );
};

export default AssignExam;
